/* eslint-disable graphql/template-strings */
import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import gql from 'graphql-tag';
import { EChartsOption } from 'echarts';
import { addDays, format, subDays } from 'date-fns';

import {
  CurrentEnergyPowerSystemState,
  CurrentPowerSystemState,
  GraphTelemetryMode,
  SystemEnergyState,
  SystemPowerState,
} from '../models/telemetry.model';
import {
  BuildDeviceFwInfoQuery,
  BuildDeviceLastAggregateQuery,
  BuildDeviceRangeEnergiesQuery,
  BuildDeviceStatusLastAggregateQuery,
  BuildEnergyStateQuery,
  BuildFullTelemetryQuery,
  BuildPowerStateQuery,
  BuildTelemetryStatusQuery,
  BuildVirtualSystemRangePowerQuery,
  BuildVirtualSystemTsQuery,
  DefaultInverterEnergyAggregateQuery,
  DefaultInverterPowerStateQuery,
  DefaultInverterPowerStateQueryForBlock,
  DefaultInverterSnapshotEnergyStateQuery,
  DefaultLastAggregateStatusQuery,
  tsQuery,
} from '../queries/query-constructors.gql';
import {
  BuildEnergyState,
  BuildPowerFlow,
  clone,
  getMeterSerial,
  getTelemetryDevices,
  getTelemetryRange,
  getTelemetryRangeFromTo,
  getTelemetryStatus,
  isNotNullOrUndefined,
  processTelemetryData,
  rangeTodayForAggregate,
  removeEmptyDevicesFromDeviceList,
  removeMaskIdFromDeviceList,
} from '../utils';
import {
  GetAggregateQuery,
  GetInverterDataByRangeQuery,
  GetInverterDataByRangeV2Query,
} from '../telemetry/index.graph-ql';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';
import {
  ConnextSystem,
  DeviceTelemetry,
  ProductModelService,
  UISystem,
  VirtualSystem,
} from '../models';
import {
  Inverter,
  InverterV2,
  Range,
  Series,
} from '../telemetry';
import {
  appBuildPowerDailyGraph,
  appBuildStateOfChargeGraph,
  appBuildStateOfSocsGraph,
  buildEnergyPieChart,
  buildEnergyWeekGraph,
  buildMonthOverview,
  buildPowerAutomationGraph,
  buildYearOverview,
  EnergySeries,
  GraphEnergyMonthHelper,
  GraphEnergyWeekHelper,
  GraphPowerHelper,
  GraphSocHelper,
  portalBuildPowerDailyGraph,
  PowerDetailsSeries,
} from '../telemetry-graph';
import { System } from '../models/firebase.model';
import { GetLastAggregateQuery } from '../telemetry/queries/get-last-aggregate.query';
import { Device } from '../models/firebase.model';

@Injectable({
  providedIn: 'root',
})
export class TelemetryService {
  readonly client$ = this.graphqlApiService.client$.pipe(isNotNullOrUndefined());

  constructor(
    private readonly getAggregateQuery: GetAggregateQuery,
    private readonly getInverterDataByRangeQuery: GetInverterDataByRangeQuery,
    private readonly getInverterDataByRangeV2Query: GetInverterDataByRangeV2Query,
    private readonly getLastAggregateQuery: GetLastAggregateQuery,
    private readonly graphqlApiService: GraphqlApiService,
  ) {}

  /** CURRENT STATE */
  async getState(uiSystem: ConnextSystem | VirtualSystem, refresh = false): Promise<CurrentEnergyPowerSystemState> {
    if (uiSystem instanceof ConnextSystem) {
      return (await this.getConnextCurrentSystemState(uiSystem, refresh));
    }
    return (await this.getVirtualCurrentSystemState(uiSystem, refresh));
  }

  /** CURRENT STATE OF SINGLE DEVICES IN PLANT */
  async getDevicesState(
    uiSystem: ConnextSystem | VirtualSystem,
    range: string,
    refresh = false,
  ): Promise<{ [key: string ]: Partial<Inverter>[] }> {
    if (uiSystem instanceof ConnextSystem) {
      const devices = ((await uiSystem.data$.pipe(first()).toPromise()).devices)?.filter((device) => device.sn);
      const promises = devices?.map(
        async (device)=> ( (await this.getDevicePowerTelemetryGraphOptions(device.sn,range,uiSystem,refresh)).raw));
      const telemetries =  await Promise.all(promises!);
      return  Object.assign({}, ...telemetries);

    }
    return (await this.getVirtualSystemPowerTelemetryGraphOptions(uiSystem, range, refresh)).raw;
  }

  async getCustomDailyState(
    uiSystem: ConnextSystem | VirtualSystem,
    date: string,
    refresh = false,
  ): Promise<CurrentEnergyPowerSystemState> {
    if (uiSystem instanceof ConnextSystem) {
      return (await this.getConnextSystemState(uiSystem, date, refresh));
    }
    return (await this.getVirtualSystemState(uiSystem, date, refresh));
  }

  /**
   * Retrieve powers and energies for connext systems, telemetry has primary key `zz${connextId}z`
   *
   * @param uiSystem - The ConnextSystem object for which to fetch  telemetries.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a CurrentEnergyPowerSystemState object containing the connext system state.
   */
  async getConnextSystemState(
    uiSystem: ConnextSystem,
    date?: string,
    refresh = false,
  ): Promise<CurrentEnergyPowerSystemState> {

    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    const rtRange = date ? getTelemetryRangeFromTo(date, date, tz.toString()) : undefined;

    let aggregateRange: Range | undefined;
    if (rtRange && date) {
      aggregateRange = { from: date, to: rtRange.to };
    }

    const powerStatePromise = this.getConnextSystemPowerState(uiSystem, aggregateRange, refresh);
    const energyStatePromise = this.getConnextSystemEnergyState(uiSystem, aggregateRange, refresh);

    const [powerState, energyState] = await Promise.all([powerStatePromise, energyStatePromise]);

    return { powerState, energyState };
  }
  /**
   * Retrieve powers and energies for connext systems.
   *
   * @param uiSystem - The ConnextSystem object for which to fetch  telemetries.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a CurrentEnergyPowerSystemState object containing the connext system state.
   */
  async getConnextSystemPowerState(
    uiSystem: ConnextSystem,
    range?: Range,
    refresh = false,
  ): Promise<SystemPowerState> {
    const connextId = uiSystem.id;
    const serial = `zz${connextId}z`.toUpperCase();
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    range = range ?? rangeTodayForAggregate(`${tz}`);

    const { data: { getAggregate: cT } } = clone(await this.getAggregateQuery.execute(
      gql`${DefaultInverterEnergyAggregateQuery}`,
      {
        serial,
        range,
        series: Series.DAY,
        type: 'Connext',
      },
      refresh,
    )) as unknown as { data: { getAggregate: Partial<Inverter>[] } };

    return BuildPowerFlow(cT[0] || {});
  }
  /**
   * Retrieve powers and energies for connext systems.
   *
   * @param uiSystem - The ConnextSystem object for which to fetch  telemetries.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a CurrentEnergyPowerSystemState object containing the connext system state.
   */
  async getConnextSystemEnergyState(
    uiSystem: ConnextSystem,
    range?: Range,
    refresh = false,
  ): Promise<SystemEnergyState | undefined> {
    const cT = await this.fetchConnextEnergyTelemetry(uiSystem, range, refresh);

    return cT.length ? BuildEnergyState(cT[0]) : undefined;
  }

  async getConnextCurrentSystemState(
    uiSystem: ConnextSystem,
    refresh = false,
  ): Promise<CurrentEnergyPowerSystemState> {
    const connextId = uiSystem.id!;
    const serial = `zz${connextId}z`.toUpperCase();

    const { data: { getLastAggregate: cT } } = clone(await this.getLastAggregateQuery.execute(
      gql`${DefaultInverterEnergyAggregateQuery}`,
      {
        serial,
        series: Series.DAY,
        type: 'Connext',
      },
      refresh,
    )) as unknown as { data: { getLastAggregate: Partial<InverterV2>[] } };

    const powerState = cT?.length > 0 ? BuildPowerFlow(cT[0]) : undefined
    const energyState = cT?.length > 0 ? BuildEnergyState(cT[0]) : undefined;

    const connectionStatus = cT?.length > 0 ? getTelemetryStatus([connextId], { [serial]: cT[0] }) : { checked: false };
    return {
      powerState,
      energyState,
      connectionStatus,
    };
  }

  async getVirtualCurrentSystemState(
    uiSystem: VirtualSystem,
    refresh = false,
  ): Promise<CurrentEnergyPowerSystemState> {
    const system = uiSystem.system$.value;
    const telemetryDevices = getTelemetryDevices(system);
    if (!telemetryDevices.length) {
      return { powerState: BuildPowerFlow({}), processedData: {} };
    }

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const inverterGwType = uiSystem.type === 'virtual' ? 'Solarman' : 'Connext';

    const query = gql`${BuildDeviceLastAggregateQuery(telemetryDevices, inverterGwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: InverterV2[] }>({ query, fetchPolicy }));
    const rawTelemetry: { [key: string]: Partial<InverterV2> } = {};

    Object.keys(data).forEach((key) => {
      if (data[key] && data[key][0] !== null && data[key][0] !== undefined) {
          rawTelemetry[key] = data[key][0];
      } else {
          rawTelemetry[key] = {};
      }
    });

    const telemetryPowerAggregate = await uiSystem.getSystemPowerAggregate(
      removeMaskIdFromDeviceList(clone(rawTelemetry)),
    ).pipe(first()).toPromise();

    const telemetryEnergyAggregate = await uiSystem.getSystemEnergyAggregate(
      removeMaskIdFromDeviceList(clone(rawTelemetry)),
    ).pipe(first()).toPromise();

    const energyState =  BuildEnergyState(telemetryEnergyAggregate);
    const powerState = BuildPowerFlow({...telemetryPowerAggregate});

    const devices = uiSystem.system$.value.devices.map((device: Device) => device.id);
    const telemetryStatus = getTelemetryStatus(devices, rawTelemetry)

    return { powerState, energyState, connectionStatus: telemetryStatus };
  }

  /**
   * Retrieve powers and energies for virtual systems.
   *
   * @param uiSystem - The VirtualSystem object for which to fetch  telemetries.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a CurrentEnergyPowerSystemState object containing the virtual system state.
   */
  async getVirtualSystemState(
    uiSystem: VirtualSystem,
    date?: string,
    refresh = false,
  ): Promise<CurrentEnergyPowerSystemState> {
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    const rtRange = date ? getTelemetryRangeFromTo(date, date, tz.toString()) : undefined;

    let aggregateRange: Range | undefined;
    if (rtRange && date) {
      aggregateRange = { from: date, to: rtRange.to };
    }

    const powerStatePromise = this.getVirtualSystemPowerState(uiSystem, aggregateRange, refresh);
    const energyStatePromise = this.getVirtualSystemEnergyState(uiSystem, aggregateRange, refresh);

    const [powerState, energyState] = await Promise.all([powerStatePromise, energyStatePromise]);

    return { powerState: powerState?.powerState, energyState , processedData: powerState?.processedData };
  }

  /**
   * Retrieve daily and total power telemetry for virtual systems.
   *
   * @param uiSystem - The VirtualSystem object for which to fetch energy telemetry.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a CurrentPowerSystemState object containing daily and total power telemetry and raw telemetry.
   */
  async getVirtualSystemPowerState(
    uiSystem: VirtualSystem,
    range?: Range,
    refresh = false,
  ): Promise<CurrentPowerSystemState> {
    const telemetryDevices = getTelemetryDevices(uiSystem.system$.value);
    if (!telemetryDevices.length) {
      return { powerState: BuildPowerFlow({}), processedData: {} };
    }

    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    range = range ?? rangeTodayForAggregate(`${tz}`);

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    const query = gql`${BuildPowerStateQuery(telemetryDevices, range, 'Solarman')}`;
    const { data } = clone(await client.query<{ [key: string ]: Inverter[] }>({ query, fetchPolicy }));
    const rawTelemetry: { [key: string]: Partial<Inverter> } = {};

    Object.keys(data).forEach((key) => {
      if (data[key] && data[key][0] !== null && data[key][0] !== undefined) {
          rawTelemetry[key] = data[key][0];
      } else {
          rawTelemetry[key] = {};
      }
    });

    const processedData: { [key: string ]: Partial<Inverter> } = await processTelemetryData(rawTelemetry);


    const telemetryPowerAggregate = await uiSystem.getSystemPowerAggregate(
      removeMaskIdFromDeviceList(clone(rawTelemetry)),
    ).pipe(first()).toPromise();

    const cT = { ...telemetryPowerAggregate};
    return { powerState: BuildPowerFlow(cT), processedData };
  }
  /**
   * Retrieve daily and total energy telemetry for virtual systems.
   *
   * @param uiSystem - The VirtualSystem object for which to fetch energy telemetry.
   * @param range - (Optional) The time range for the telemetry data.
   *              If not provided, the default range is today's date in the system's timezone.
   * @param refresh - (Optional) A flag indicating whether to force data refresh from the network (true) or use the cached data (false).
   *                Default is false.
   * @returns A Promise resolving to a SystemEnergyState object containing daily and total energy telemetry.
   */
  async getVirtualSystemEnergyState(
    uiSystem: VirtualSystem,
    range?: Range,
    refresh = false,
  ): Promise<SystemEnergyState> {
    const data = await this.fetchVirtualEnergyTelemetry(uiSystem, range, refresh);
    const rawTelemetry: { [key: string]: Partial<Inverter> } = {};

    Object.keys(data).forEach((key) => {
      if (data[key] && data[key][0] !== null && data[key][0] !== undefined) {
          rawTelemetry[key] = data[key][0];
      } else {
          rawTelemetry[key] = {};
      }
    });

    const telemetryEnergyAggregate = await uiSystem.getSystemEnergyAggregate(
      removeMaskIdFromDeviceList(clone(rawTelemetry)),
    ).pipe(first()).toPromise();

    return BuildEnergyState(telemetryEnergyAggregate);
  }

  async getSystemSocGraphOptions(
    uiSystem: ConnextSystem | VirtualSystem,
    date: string,
    refresh = false,
    ): Promise<{options: EChartsOption;devices: PowerDetailsSeries[]}> {
      const deviceTelemetry= await  this.getDevicesState(uiSystem,date,refresh);
      const tz = await uiSystem.getTz().pipe(first()).toPromise();
      const utcDate = new Date(date);
      utcDate.setHours(utcDate.getHours() - utcDate.getTimezoneOffset() / 60);
      const graphsHelper = new GraphSocHelper(deviceTelemetry,utcDate,tz);
      return graphsHelper.getGraphOptions(appBuildStateOfSocsGraph,tz);
  }
  /**
   * POWER TELEMETRY
   * - virtual systems with multiple telemetry devices
   */
  async getVirtualSystemPowerTelemetryGraphOptions(
    uiSystem: ConnextSystem | VirtualSystem,
    date: string,
    refresh = false,
  ): Promise<{
    dailyPowerState: EChartsOption;
    dailySocState?: EChartsOption;
    raw: { [key: string]: Partial<InverterV2>[] };
  }> {
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    const {raw , cT} = await this.fetchVirtualSystemPowerTelemetry(uiSystem, date, refresh, tz);

    const utcDate = new Date(date);
    utcDate.setHours(utcDate.getHours() - utcDate.getTimezoneOffset() / 60);
    const graph = new GraphPowerHelper(cT, utcDate, tz);
    return {
      ...graph.getGraphOptions(appBuildPowerDailyGraph, buildPowerAutomationGraph, appBuildStateOfChargeGraph, tz),
      raw,
    };
  }

  /**
   * POWER TELEMETRY
   * Connext (zz${connextId}z) OR single device
   */
  async getPowerTelemetryGraphOptions(
    deviceId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    refresh = false,
  ): Promise<{
    dailyPowerState: EChartsOption;
    dailySocState?: EChartsOption;
    raw: { [key: string]: Partial<InverterV2>[] };
  }> {
    const product = new ProductModelService(deviceId);
    const telemetry = product.type?.name === 'connext'
      ? this.getConnextPowerTelemetryGraphOptions.bind(this)
      : this.getDevicePowerTelemetryGraphOptions.bind(this);
    return await telemetry(deviceId, date, uiSystem, refresh);
  }

  /**
   * POWER TELEMETRY
   * - Connext (zz${connextId}z)
   */
  async getConnextPowerTelemetryGraphOptions(
    connextId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    refresh = false,
  ): Promise<{
    dailyPowerState: EChartsOption;
    dailySocState?: EChartsOption;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    const cT = await this.fetchConnextPowerTelemetry(uiSystem, date, refresh, tz);

    const utcDate = new Date(date);
    utcDate.setHours(utcDate.getHours() - utcDate.getTimezoneOffset() / 60);
    const graph = new GraphPowerHelper(cT, utcDate, tz);
    return {
      ...graph.getGraphOptions(appBuildPowerDailyGraph, buildPowerAutomationGraph, appBuildStateOfChargeGraph, tz),
      raw: { [connextId]: cT },
    };
  }

  /**
   * POWER TELEMETRY
   * - single device
   * (logic similar to the one in the system classes ConnextSystem and VirtualSystem)
   */
  async getDevicePowerTelemetryGraphOptions(
    deviceId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    refresh = false,
  ): Promise<{
    dailyPowerState: EChartsOption;
    dailySocState?: EChartsOption;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    const { telemetry , raw} = await this.fetchDevicePowerTelemetry(deviceId, date, uiSystem, tz, refresh);
    const utcDate = new Date(date);
    utcDate.setHours(utcDate.getHours() - utcDate.getTimezoneOffset() / 60);
    const graph = new GraphPowerHelper(telemetry, utcDate, tz);
    return {
      ...graph.getGraphOptions(appBuildPowerDailyGraph, buildPowerAutomationGraph, appBuildStateOfChargeGraph, tz),
      raw: { [deviceId]: raw },
    };
  }

  /**
   * ENERGY TELEMETRY (For systems)
   * - Connext system (zz${connextId}z)
   * - Virtual system
   */
  async getEnergyTelemetryGraphOptions(
    uiSystem: ConnextSystem | VirtualSystem,
    range: Range,
    buildGraphFn: any,
    mode: GraphTelemetryMode = 'asDays',
    refresh = false,
  ): Promise<{
    chartOptionsFn: any;
    overviewChartOptionsFn?: any;
    series?: EnergySeries;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    if (uiSystem instanceof ConnextSystem) {
      return this.getConnextEnergyTelemetryGraphOptions(uiSystem, range, buildGraphFn, mode, refresh);
    }
    return this.getVirtualEnergyTelemetryGraphOptions(uiSystem, range, buildGraphFn, mode, refresh);
  }

  /**
   * ENERGY TELEMETRY (For devices)
   * - single device
   */
  async getDeviceEnergyTelemetryGraphOptions(
    device: string,
    range: Range,
    uiSystem: VirtualSystem | ConnextSystem,
    buildGraphFn: any,
    mode: GraphTelemetryMode = 'asDays',
    refresh = false,
  ): Promise<{
    chartOptionsFn: any;
    overviewChartOptionsFn?: any;
    series?: EnergySeries;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const { data, energies } =  await this.fetchDeviceEnergyTelemetry(device, range, uiSystem, refresh);
    const graph =  mode === 'asDays'
      ? new GraphEnergyWeekHelper(clone(energies), clone(range))
      : new GraphEnergyMonthHelper(clone(energies), clone(range));

    const buildOverviewGraph = mode === 'asDays' ? buildMonthOverview : buildYearOverview;

    return {
      ...graph.getGraphFnOptions(buildGraphFn, buildOverviewGraph),
      raw: data,
    };
  }

  /**
   * ENERGY TELEMETRY (For Connext systems)
   * - Connext system (zz${connextId}z)
   */
  async getConnextEnergyTelemetryGraphOptions(
    uiSystem: ConnextSystem,
    range: Range,
    buildGraphFn: any,
    mode: GraphTelemetryMode = 'asDays',
    refresh = false,
  ): Promise<{
    chartOptionsFn: any;
    overviewChartOptionsFn?: any;
    series?: EnergySeries;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const telemetryId = `zz${uiSystem.connextId}z`.toUpperCase();
    const cT = await this.fetchConnextEnergyTelemetry(uiSystem, clone(range), refresh);
    const graph = mode === 'asDays' ? new GraphEnergyWeekHelper(cT, clone(range)) : new GraphEnergyMonthHelper(cT, clone(range));
    const buildOverviewGraph = mode === 'asDays' ? buildMonthOverview : buildYearOverview;
    return {
      ...graph.getGraphFnOptions(buildGraphFn, buildOverviewGraph),
      raw: { [telemetryId]: cT },
    };
  }

  /**
   * ENERGY TELEMETRY (For Virtual systems)
   * - Virtual system (aggregate of multiple devices)
   */
  async getVirtualEnergyTelemetryGraphOptions(
    uiSystem: VirtualSystem,
    range: Range,
    buildGraphFn: any,
    mode: GraphTelemetryMode = 'asDays',
    refresh = false,
  ): Promise<{
    chartOptionsFn: any;
    overviewChartOptionsFn?: any;
    series?: EnergySeries;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const telemetryDevices = getTelemetryDevices(uiSystem.system$.value);
    if (!telemetryDevices.length) {
      return { chartOptionsFn: () => ({}), raw: {} };
    }

    const rawTelemetry: { [key: string]: Partial<Inverter>[] } =  await this.fetchVirtualEnergyTelemetry(uiSystem, range, refresh);

    const telemetryAggregate = await uiSystem.getEnergyAggregate(rawTelemetry).pipe(first()).toPromise();
    const graph =  mode === 'asDays'
      ? new GraphEnergyWeekHelper(telemetryAggregate, clone(range))
      : new GraphEnergyMonthHelper(telemetryAggregate, clone(range));

    const buildOverviewGraph = mode === 'asDays' ? buildMonthOverview : buildYearOverview;
    return {
      ...graph.getGraphFnOptions(buildGraphFn, buildOverviewGraph),
      raw: rawTelemetry,
    };
  }

   /** POWER TELEMETRY - virtual systems with multiple telemetry devices */
  async getMultipleDevicesPowerTelemetryGraphOptions(system: System, date: string, tz: string, refresh = false): Promise<{
    dailyPowerState: EChartsOption;
    dailySocState?: EChartsOption;
    raw: { [key: string]: Partial<Inverter>[] };
  }> {
    const telemetryDevices = getTelemetryDevices(system);
    if (!telemetryDevices.length) {
      return { dailyPowerState: {}, raw: {} };
    }

    const range = getTelemetryRange(date, tz);

    const query = gql`${BuildVirtualSystemRangePowerQuery(telemetryDevices, range)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const result = await client.query<{ [key: string ]: Array<Partial<Inverter>> }>({ query, fetchPolicy });

    const isEmpty = Object.values(result.data).every((value) => value.length === 0);
    let cT: Partial<Inverter>[] = [];
    if ( !isEmpty ) {
      const uiSystem = new VirtualSystem(system);
      cT = await uiSystem.getDailyPowerTelemetry(
        removeEmptyDevicesFromDeviceList(removeMaskIdFromDeviceList(result.data)),
        new Date(date),
        Number(tz),
      ).pipe(first()).toPromise();
    }

    const utcDate = new Date(date);
    utcDate.setHours(utcDate.getHours() - utcDate.getTimezoneOffset() / 60);
    const graph = new GraphPowerHelper(cT, utcDate, tz);
    return {
      ...graph.getGraphOptions(portalBuildPowerDailyGraph, buildPowerAutomationGraph, appBuildStateOfChargeGraph, tz),
      raw: result.data,
    };
  }

  /**
   * SELF CONSUMPTION TELEMETRY (For systems)
   */
  async getEnergiesTelemetryGraphOptions(
    uiSystem: ConnextSystem | VirtualSystem,
    range: Range,
    buildGraphFn: any,
    mode: GraphTelemetryMode = 'asDays',
    refresh = false,
  ) {
    let telemetryAggregate;
    if (uiSystem instanceof ConnextSystem) {
      const telemetry = await this.getConnextEnergyTelemetryGraphOptions(uiSystem, clone(range),buildGraphFn, mode, refresh);
      const { raw } = telemetry;
      telemetryAggregate = raw[`ZZ${uiSystem.connextId.toUpperCase()}Z`];
    } else {
      const telemetry = await this.getVirtualEnergyTelemetryGraphOptions(uiSystem, clone(range),buildGraphFn, mode, refresh);
      const { raw } = telemetry;
      telemetryAggregate = await uiSystem.getEnergyAggregate(raw).pipe(first()).toPromise();
    }

    const graph = new GraphEnergyWeekHelper(telemetryAggregate, clone(range));
    const selfConsume = graph.getEnergies();
    return {
      chartOptionsFn: buildEnergyWeekGraph(Object.values(selfConsume), graph.periodDates),
      aggregateSelfConsumePieOptionFn: buildEnergyPieChart(Object.values(selfConsume)),
      series: selfConsume,
    };
  }

  async fetchVirtualEnergyTelemetry(
    uiSystem: VirtualSystem,
    range?: Range,
    refresh = false,
  ) : Promise<{ [key: string ]: Partial<Inverter>[] }>
  {
    const telemetryDevices = getTelemetryDevices(uiSystem.system$.value);
    if (!telemetryDevices.length) {
      return {};
    }

    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    range = range ?? rangeTodayForAggregate(`${tz}`);

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    const inverterGwType = uiSystem.type === 'virtual' ? 'Solarman' : 'Connext';
    const query = gql`${BuildEnergyStateQuery(telemetryDevices, range, inverterGwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: Inverter[] }>({ query, fetchPolicy }));

    return removeMaskIdFromDeviceList(data);
  }

  async fetchConnextEnergyTelemetry(uiSystem: ConnextSystem, range: Range | undefined, refresh: boolean): Promise<Partial<Inverter>[]> {
    const connextId = uiSystem.id;
    const serial = `zz${connextId}z`.toUpperCase();
    const tz = await uiSystem.getTz().pipe(first()).toPromise();
    range = range ?? rangeTodayForAggregate(`${tz}`);

    const { data: { getAggregate: cT } } = clone(await this.getAggregateQuery.execute(
        gql`${DefaultInverterEnergyAggregateQuery}`,
        {
          serial,
          range,
          series: Series.DAY,
          type: 'Connext',
        },
        refresh,
      )) as unknown as { data: { getAggregate: Partial<Inverter>[] } };

      return cT;
  }

  async fetchConnextPowerTelemetry(
    uiSystem: ConnextSystem | VirtualSystem,
    date: string,
    refresh: boolean,
    tz: string
  ): Promise<Partial<Inverter>[]> {
    const connextId = uiSystem.id;
    const telemetryId = `zz${connextId}z`.toUpperCase();
    const range = getTelemetryRange(date, tz);

    const { data: { getInverterDataByRangeV2 } } = clone(await this.getInverterDataByRangeV2Query.execute(
      gql`${DefaultInverterPowerStateQuery}`,
      { serial: telemetryId, range },
      refresh,
    ));
    let cT = getInverterDataByRangeV2 as Partial<InverterV2>[];
    cT = uiSystem.filterBySourceData(cT);
    return uiSystem.filterSomeAttributesFromPackets(cT, [
      'P_B1',
      'P_B2',
      'ALERTS',
      'P_SELF',
      'BP1',
      'BP2',
      'GwId',
      'P_EXT',
    ]);
  }

  async fetchVirtualSystemPowerTelemetry(
    uiSystem: ConnextSystem | VirtualSystem,
    date: string,
    refresh: boolean,
    tz: string): Promise<{
      cT: Partial<Inverter>[],
      raw: { [key: string]: Partial<InverterV2>[] }
    }>  {
    const telemetryDevices = getTelemetryDevices(uiSystem.system$.value);
    if (!telemetryDevices.length) {
      return { cT: [], raw: {} };
    }

    const range = getTelemetryRange(date, tz);

    const query = gql`${BuildVirtualSystemRangePowerQuery(telemetryDevices, range)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const result = clone(await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy }));
    const raw = uiSystem.filterBySourceData(result.data);

    const isEmpty = Object.values(raw).every((value) => value.length === 0);
    let cT: Partial<Inverter>[] = [];
    if ( !isEmpty ) {
      cT = await uiSystem.getDailyPowerTelemetry(
        removeEmptyDevicesFromDeviceList(removeMaskIdFromDeviceList(raw)),
        new Date(date),
        Number(tz),
      ).pipe(first()).toPromise();
    }
    return {
      cT,
      raw,
    }
  }

  // TODO: Refactor this method to merge with fetchDevicePowerTelemetryForBlock
  async fetchDevicePowerTelemetry(
    deviceId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    tz: string,
    refresh = false,
  ): Promise<{
    telemetry: Partial<InverterV2>[],
    raw: Partial<InverterV2>[],
  }> {
    const { filteredByGw, raw, device } = await this.fetchRawDevicePowerTelemetry(deviceId, date, uiSystem, tz, refresh);
    return  {
      telemetry: device.getPowerTelemetry(filteredByGw, [
        'P_EXT',
        'ALERTS',
        'P_SELF',
        'GwId',
        'P_B1',
        'P_B2',
        'BP1',
        'BP2',
      ]) as Partial<InverterV2>[],
      raw,
    }
  }

  async fetchDevicePowerTelemetryForBlock(
    deviceId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    tz: string,
    refresh = false,
  ): Promise<{
    telemetry: Partial<InverterV2>[],
    raw: Partial<InverterV2>[],
  }> {
    const { filteredByGw, raw, device } = await this.fetchRawDevicePowerTelemetry(deviceId, date, uiSystem, tz, refresh, DefaultInverterPowerStateQueryForBlock);
    return  {
      telemetry: device.getPowerTelemetry(filteredByGw, []) as Partial<InverterV2>[],
      raw,
    }
  }

  async fetchRawDevicePowerTelemetry(
    deviceId: string,
    date: string,
    uiSystem: ConnextSystem | VirtualSystem,
    tz: string,
    refresh = false,
    query = DefaultInverterPowerStateQuery,
  ): Promise<{
    filteredByGw: Partial<InverterV2>[],
    raw: Partial<InverterV2>[],
    device: DeviceTelemetry,
  }> {
    let telemetryId = deviceId.toUpperCase();
    if ((new ProductModelService(telemetryId)).type?.name === 'meter') {
      telemetryId = getMeterSerial(telemetryId);
    }

    const range = getTelemetryRange(date, tz);
    const { data: { getInverterDataByRangeV2 } } = clone(await this.getInverterDataByRangeV2Query.execute(
      gql`${query}`,
      { serial: telemetryId, range },
      refresh,
    ));
    const cT = getInverterDataByRangeV2 as Partial<InverterV2>[] || [];
    const device = new DeviceTelemetry(deviceId, uiSystem);
    const filteredByGw = device.filterBySourceData(cT);
    return  {
      device,
      filteredByGw,
      raw: cT,
    }
  }

  // to fetch connext energy every 5 minute.
  async fetchConnextSnapshotEnergyTelemetry(connextId: string, range: Range, tz: string) {
    const telemetryId = `zz${connextId}z`.toUpperCase();
    const gqlQuery = gql`${DefaultInverterSnapshotEnergyStateQuery}`;
    const result = await this.getInverterDataByRangeQuery.execute(gqlQuery,
      { serial: telemetryId, range },
      false,
    );
    const cT = result.data.getInverterDataByRange as Partial<Inverter>[];

    return cT;
  }

  async fetchConnextTsTelemetry(connextId: string, refresh: boolean) {
    const telemetryId = `zz${connextId}z`.toUpperCase();
    const gqlQuery = gql`${tsQuery}`;

    const range = {
      from: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      to: format(addDays(new Date(), 1), 'yyyy-MM-dd') + 'T23:59:59.000Z',
    }

    const result = await this.getAggregateQuery.execute(gqlQuery,
      { serial: telemetryId, range, series: Series.DAY, type: 'Connext' },
      refresh,
    );

    return (result.data.getAggregate as Partial<Inverter>[]).sort(
      (a, b) => (b.TS || 0) - (a.TS || 0),
    ).slice(0, 1);
  }

  async fetchVirtualSystemTsTelemetry(
    uiSystem: ConnextSystem | VirtualSystem,
    refresh: boolean,
  ): Promise<{
      raw: { [key: string]: Partial<InverterV2>[] }
    }>  {
    const telemetryDevices = getTelemetryDevices(uiSystem.system$.value);
    if (!telemetryDevices.length) {
      return { raw: {} };
    }

    const range = {
      from: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      to: format(addDays(new Date(), 1), 'yyyy-MM-dd') + 'T23:59:59.000Z',
    }

    const query = gql`${BuildVirtualSystemTsQuery(telemetryDevices, range)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const result = clone(await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy }));
    const raw = result.data;

    for (const key in raw) {
      raw[key] = raw[key].sort((a, b) => (b.TS || 0) - (a.TS || 0)).slice(0, 1);
    }

    return {
      raw,
    }
  }

  async fetchDeviceFwInfo(deviceId: string, uiSystem: UISystem, refresh = false) {
    const inverterGwType = uiSystem.type === 'virtual' ? 'Solarman' : 'Connext';

    const query = gql`${BuildDeviceFwInfoQuery(deviceId, inverterGwType)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    const { data } = await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy });
    const results = Object.values(data);
    const values = results.length ? results[0] : [];
    // sort by TS
    values.sort((a, b) => (b.TS || 0) - (a.TS || 0));
    const latest = values[0] || {};

    return {
      raw: latest,
    }
  }

  async fetchDeviceEnergyTelemetry(
    deviceId: string,
    range: Range,
    uiSystem: ConnextSystem | VirtualSystem,
    refresh = false,
  ): Promise<{
    data: { [key: string]: Partial<Inverter>[] },
    energies: Partial<Inverter>[],
  }> {
    const serial = (new ProductModelService(deviceId).type?.name === 'meter') ? getMeterSerial(deviceId) : deviceId.toUpperCase();
    const query = gql`query { ${BuildDeviceRangeEnergiesQuery(serial, range, Series.DAY, uiSystem)} }`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const { data } = await client.query<{ [key: string ]: Array<Partial<Inverter>> }>({ query, fetchPolicy });
    const cT = removeMaskIdFromDeviceList(clone(data));
    const d = new DeviceTelemetry(serial, uiSystem);
    const energies = d.getEnergyTelemetry(cT[deviceId.toUpperCase()]);
    return { data: cT, energies };
  }

  async fetchFullTelemetry(
    deviceIds: string[],
    date: string,
    refresh: boolean,
    tz: string): Promise<{
      [key: string]: Partial<InverterV2>[];
  }> {
    const range = getTelemetryRange(date, tz);

    const query = gql`${BuildFullTelemetryQuery(deviceIds.map((id) => ({ id })), range)}`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    const result = clone(await client.query<{ [key: string ]: Array<Partial<InverterV2>> }>({ query, fetchPolicy }));
    return removeMaskIdFromDeviceList(result.data);
  }

  async checkIfExist(id: string) {
    const serial = id.toUpperCase();
    // eslint-disable-next-line graphql/template-strings
    const query = gql`query { getInverterData(serial: "${serial}") {
      TS
    } }`;
    const client = await this.client$.pipe(first()).toPromise();
    const result = await client.query<{ getInverterData: Partial<Inverter> }>({ query, fetchPolicy: 'network-only' });
    const cT = result.data.getInverterData as Partial<Inverter>;
    return cT.TS;
  }

  async getGenericTelemetryStatus(system: UISystem, refresh = true) {
    const telemetryDevices = getTelemetryDevices(system.system$.value);
    if (!telemetryDevices.length) {
      return {};
    }
    const inverterGwType = system.type === 'virtual' ? 'Solarman' : 'Connext';

    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';

    const query = gql`${BuildDeviceStatusLastAggregateQuery(telemetryDevices, inverterGwType)}`;
    const { data } = clone(await client.query<{ [key: string ]: InverterV2[] }>({ query, fetchPolicy }));
    const rawTelemetry: { [key: string]: Partial<InverterV2> } = {};

    Object.keys(data).forEach((key) => {
      if (data[key] && data[key][0] !== null && data[key][0] !== undefined) {
          rawTelemetry[key] = data[key][0];
      } else {
          rawTelemetry[key] = {};
      }
    });

    return removeMaskIdFromDeviceList(rawTelemetry);
  }

  async getConnextTelemetryStatus(connextId: string, refresh = true): Promise<{[key: string]: Partial<Inverter>}> {
    const serial = `zz${connextId}z`.toUpperCase();
    const { data: { getLastAggregate: cT } } = clone(await this.getLastAggregateQuery.execute(
      gql`${DefaultLastAggregateStatusQuery}`,
      {
        serial,
        series: Series.DAY,
        type: 'Connext',
      },
      refresh,
    )) as unknown as { data: { getLastAggregate: Partial<InverterV2>[] } };

    return { [connextId]: {TS: cT?.length ? cT[0].TS : 0, GwId: connextId} };
  }

  async fetchVirtualStatusTelemetry(system: System, refresh = true): Promise<{[key: string]: Partial<InverterV2>}> {
    const telemetryDevices = getTelemetryDevices(system);
    if (!telemetryDevices.length) {
      return {};
    }

    const queryString = BuildTelemetryStatusQuery(telemetryDevices);
    const query = gql`query { ${queryString} }`;
    const client = await this.client$.pipe(first()).toPromise();
    const fetchPolicy = refresh ? 'network-only' : 'cache-first';
    let { data } = await client.query<{ [key: string ]: Partial<InverterV2> }>({ query, fetchPolicy });
    data = removeMaskIdFromDeviceList(data);
    return data;
  }
}
